import { useEffect, useMemo, useState } from "react";
import { useTran } from "@/hooks";
import { useNavigate } from "react-router-dom";
import { PageHeader } from "@/components/common";
import { Button } from "@/components/ui/button";
import { frameworkInfos } from "@/lib/constants/frameworks";
import { PATHS } from "@/lib/router";
import { cn } from "@/lib/utils";

const EsgForm = () => {
  const { t } = useTran();
  const navigate = useNavigate();
  const [active, setActive] = useState("SGX");

  useEffect(() => {
    if (frameworkInfos.length > 0) {
      setActive(frameworkInfos[0].key);
    }
  }, []);

  const activeData = useMemo(
    () =>
      frameworkInfos.find((item) => item.key === active) || frameworkInfos[0],
    [active],
  );

  const activePoints = useMemo(() => activeData?.points || [], [activeData]);

  const canCreate = useMemo(() => !activeData?.disabled, [activeData]);

  const handleCreate = () => {
    if (!canCreate) return;
    const path = PATHS.ESG_REPORT.replace(":framework", activeData.key).replace(
      ":stepId",
      1,
    );
    navigate(path);
  };

  return (
    <div className="space-y-4">
      <PageHeader
        title={t("ESG - Reporting Frameworks")}
        description={t("Review frameworks and choose the one to start with.")}
      />
      <div className="flex flex-col gap-6">
        {/* Top Grid of Framework Cards */}
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
          {frameworkInfos.map((framework) => {
            const isActive = framework.key === active;
            return (
              <button
                key={framework.key}
                type="button"
                onClick={() => setActive(framework.key)}
                className={cn(
                  "group relative flex w-full flex-col items-center justify-center gap-3 rounded-radius border px-3 py-4 text-center shadow-sm ring-1 ring-transparent transition hover:-translate-y-[1px] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
                  isActive
                    ? "border-primary/70 bg-primary/10 ring-primary/30"
                    : "border-border bg-background/40 hover:border-primary/40 hover:bg-muted/20 hover:ring-primary/15",
                )}
              >
                {/* Logo Container */}
                <div className="flex h-12 w-12 items-center justify-center">
                  {framework.image ? (
                    <img
                      src={framework.image}
                      alt={`${framework.value} logo`}
                      className="h-full w-full object-contain"
                    />
                  ) : (
                    <div className="h-8 w-8 rounded-radius bg-muted/60 animate-pulse" />
                  )}
                </div>

                {/* Titles */}
                <div className="space-y-1">
                  <h3
                    className={cn(
                      "text-sm font-bold uppercase tracking-wider",
                      isActive ? "text-primary" : "text-foreground",
                    )}
                  >
                    {framework.key}
                  </h3>
                  <p className="text-[10px] text-muted-foreground line-clamp-1">
                    {framework.value}
                  </p>
                </div>
              </button>
            );
          })}
        </div>
        {/* Detailed Info Section Below */}
        <section className="relative rounded-2xl border border-border bg-muted/20 px-3 py-4">
          <div className="flex flex-col gap-6">
            <div className="flex flex-col-2 gap-12">
              {/* Header with Icon */}
              <div className="flex items-center gap-4">
                {activeData?.image && (
                  <div className="flex h-30 w-30">
                    <img
                      src={activeData.image}
                      alt="icon"
                      className="size-40"
                    />
                  </div>
                )}
              </div>

              {/* Description and Lists */}
              <div className="space-y-4 max-w-3xl">
                <h3 className="text-xl font-semibold text-foreground">
                  {activeData?.value} Report
                </h3>
                <p className="text-sm text-muted-foreground">
                  {activeData?.summary}
                </p>
                <p className="text-sm italic text-muted-foreground">
                  {activeData?.usage}
                </p>

                {activePoints.length > 0 && (
                  <ul className="space-y-3 text-sm text-muted-foreground">
                    {activePoints.map((point, i) => (
                      <li key={i} className="flex items-start gap-2">
                        <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-muted-foreground/60" />
                        {point}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>

            {/* Action Button */}
            <div className="flex justify-end border-t border-border pt-4">
              {canCreate ? (
                <Button onClick={handleCreate} size="lg">
                  {t("Create Report")}
                </Button>
              ) : (
                <Button size="lg" variant="outline" disabled>
                  {t("Pending")}
                </Button>
              )}
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default EsgForm;
